import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { SlidersHorizontal, WarningCircle, ArrowClockwise } from "@phosphor-icons/react";

const TOGGLES = [
  { key: "free_access", title: "Free access (no paywall / prices)", desc: "Trial & free accounts get full ClaroShield Light access with no expiry lock; upgrade prompts are hidden. Paid plans unchanged." },
  { key: "hide_shield", title: "Hide ClaroShield previews", desc: "Keep Shield-only modules out of sight for non-Shield plans (no locked previews)." },
  { key: "block_exports", title: "Disable exports (Word / PDF / Excel)", desc: "Blocks all downloads for customers with a “Contact ClaroShield” message. You (Super Admin) can still export." },
  { key: "block_copy", title: "Block copy/paste on generated items", desc: "Best-effort deterrent — disables selection, copy, cut and right-click for customers on generated content." },
];

export default function PlatformModePanel() {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState("");
  const [invs, setInvs] = useState(null);
  const [invBusy, setInvBusy] = useState("");

  useEffect(() => {
    api.get("/console/platform-settings").then((r) => setFlags(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const loadInvs = async () => {
    try {
      const r = await api.get("/console/investigations", { params: { open_only: true } });
      setInvs(r.data || []);
    } catch (e) {
      setInvs([]);
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load investigations — try Refresh.");
    }
  };

  useEffect(() => {
    if (flags?.cleanup_mode) loadInvs(); else setInvs(null);
  }, [flags?.cleanup_mode]);

  const forceClose = async (inv) => {
    if (!window.confirm(`Force-close "${inv.title}"? This bypasses the closure checklist and stops overdue reminders.`)) return;
    setInvBusy(inv.id);
    try {
      await api.post(`/console/investigations/${inv.id}/force-close`);
      toast.success(`Force-closed "${inv.title}".`);
      setInvs((cur) => (cur || []).filter((x) => x.id !== inv.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Force-close failed."); }
    finally { setInvBusy(""); }
  };

  const deleteInv = async (inv) => {
    if (!window.confirm(`Permanently DELETE "${inv.title}"? This cannot be undone.`)) return;
    setInvBusy(inv.id);
    try {
      await api.delete(`/console/investigations/${inv.id}`);
      toast.success(`Deleted "${inv.title}".`);
      setInvs((cur) => (cur || []).filter((x) => x.id !== inv.id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed."); }
    finally { setInvBusy(""); }
  };

  const toggle = async (key) => {
    if (!flags) return;
    const next = { ...flags, [key]: !flags[key] };
    setBusy(key); setFlags(next);
    try {
      const r = await api.put("/console/platform-settings", next);
      setFlags(r.data);
      toast.success("Platform mode updated. Customers see changes on their next page load.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setFlags(flags); }
    finally { setBusy(""); }
  };

  if (!flags) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;

  return (
    <div className="border border-border bg-card" data-testid="platform-mode-panel">
      <div className="border-b border-border px-5 py-3 flex items-center gap-2">
        <SlidersHorizontal size={18} weight="duotone" />
        <span className="overline text-muted-foreground">Platform mode</span>
      </div>
      <p className="px-5 pt-4 text-sm text-muted-foreground">
        Global controls for the testing phase. Turn off the paywall, keep ClaroShield hidden, disable exports, and
        deter copy/paste — all without touching individual accounts.
      </p>
      <div className="divide-y divide-border mt-2">
        {TOGGLES.map((t) => (
          <div key={t.key} className="flex items-start justify-between gap-4 px-5 py-4">
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-foreground">{t.title}</div>
              <div className="text-[12px] text-muted-foreground leading-snug">{t.desc}</div>
            </div>
            <Switch data-testid={`platform-toggle-${t.key}`} checked={!!flags[t.key]}
              disabled={busy === t.key} onCheckedChange={() => toggle(t.key)} />
          </div>
        ))}
      </div>
      <div className="border-t-2 border-red-300 bg-red-50/60 mt-2" data-testid="cleanup-mode-section">
        <div className="flex items-start justify-between gap-4 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-red-800 flex items-center gap-1">
              <SlidersHorizontal size={15} /> Cleanup Mode (destructive actions)
            </div>
            <div className="text-[12px] text-red-700/90 leading-snug">
              Claro Super Admin only. Unlocks <b>permanent entity deletion</b> and <b>force-closing investigations</b>
              for initial cleanup / extreme circumstances. Keep this OFF during normal operation.
            </div>
          </div>
          <Switch data-testid="platform-toggle-cleanup_mode" checked={!!flags.cleanup_mode}
            disabled={busy === "cleanup_mode"} onCheckedChange={() => toggle("cleanup_mode")} />
        </div>
        {flags.cleanup_mode && (
          <div className="mx-5 mb-4 px-3 py-2 bg-red-600 text-white text-[12px] font-semibold" data-testid="cleanup-mode-banner">
            ⚠ Cleanup Mode is ON — destructive actions (entity purge, force-close) are enabled. Turn this off when finished.
          </div>
        )}
        {flags.cleanup_mode && (
          <div className="mx-5 mb-5" data-testid="cleanup-investigations">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[13px] font-bold text-red-800">Open investigations (all entities)</div>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={loadInvs} data-testid="cleanup-invs-refresh">
                <ArrowClockwise size={13} className="mr-1" /> Refresh
              </Button>
            </div>
            <p className="text-[11px] text-red-700/80 mb-2">
              Every open investigation across all entities — including ones hidden from your normal list. Force-close to stop overdue-reminder emails, or delete to remove entirely.
            </p>
            {invs === null ? (
              <div className="text-xs text-muted-foreground py-3">Loading…</div>
            ) : invs.length === 0 ? (
              <div className="text-xs text-muted-foreground py-3" data-testid="cleanup-invs-empty">No open investigations — nothing to clean up.</div>
            ) : (
              <div className="border border-red-200 divide-y divide-red-100 bg-white max-h-[360px] overflow-y-auto">
                {invs.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`cleanup-inv-${inv.id}`}>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold truncate flex items-center gap-1.5">
                        {inv.overdue && <WarningCircle size={14} weight="fill" className="text-red-600 shrink-0" />}
                        {inv.title || inv.standard_id || inv.id}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {inv.entity_name || (inv.orphaned ? "⚠ orphaned (no/deleted entity)" : inv.registration_id)}
                        {inv.close_due_date ? ` · due ${inv.close_due_date.slice(0, 10)}` : ""}
                        {inv.lead_name ? ` · lead ${inv.lead_name}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" className="rounded-none h-7 text-xs bg-red-700 hover:bg-red-800" disabled={invBusy === inv.id}
                        onClick={() => forceClose(inv)} data-testid={`cleanup-inv-close-${inv.id}`}>Force-close</Button>
                      <Button size="sm" variant="outline" className="rounded-none h-7 text-xs border-red-300 text-red-700" disabled={invBusy === inv.id}
                        onClick={() => deleteInv(inv)} data-testid={`cleanup-inv-delete-${inv.id}`}>Delete</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
